import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { 
  Users, 
  Building2, 
  FolderKanban, 
  RotateCcw, 
  Trash2, 
  RefreshCw,
  AlertTriangle,
  Archive,
  CheckSquare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Format date to YYYY.MM.DD
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0].replace(/-/g, '.');
};

/**
 * Archived Item Card Component
 */
const ArchivedItemCard = ({ 
  item, 
  type, 
  selected,
  onSelect,
  onRestore, 
  onDelete,
  primaryField,
  secondaryField,
  tertiaryField 
}) => {
  const { t } = useTranslation();
  return (
    <div 
      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${selected ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 hover:bg-gray-100'}`}
      data-testid={`archived-item-${item.id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Checkbox
          checked={selected}
          onCheckedChange={() => onSelect(item.id)}
          aria-label={`Select ${item[primaryField] || item.name || 'item'}`}
        />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">
              {item[primaryField] || item.name || 'Unknown'}
            </span>
            {item.archived_at && (
              <Badge variant="secondary" className="text-xs">
                Archived {formatDate(item.archived_at)}
              </Badge>
            )}
          </div>
          {secondaryField && item[secondaryField] && (
            <p className="text-sm text-gray-500 truncate mt-1">
              {item[secondaryField]}
            </p>
          )}
          {tertiaryField && item[tertiaryField] && (
            <p className="text-xs text-gray-400 truncate">
              {tertiaryField === 'employee_code' || tertiaryField === 'customer_code' || tertiaryField === 'project_code' 
                ? `Code: ${item[tertiaryField]}`
                : item[tertiaryField]}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRestore(type, item.id, item[primaryField] || item.name)}
          className="text-green-600 hover:text-green-700 hover:bg-green-50"
          data-testid={`restore-btn-${item.id}`}
        >
          <RotateCcw className="h-4 w-4 mr-1" />
          {t('auto.set.restore')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(type, item.id, item[primaryField] || item.name)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          data-testid={`delete-btn-${item.id}`}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          {t('auto.set.delete')}
        </Button>
      </div>
    </div>
  );
};

/**
 * Archived Items Section Component
 */
const ArchivedItemsSection = ({ 
  title, 
  icon: Icon, 
  items, 
  type, 
  loading, 
  selectedItems,
  onSelectItem,
  onSelectAll,
  onRestore, 
  onDelete,
  onBulkRestore,
  onBulkDelete,
  primaryField,
  secondaryField,
  tertiaryField,
  emptyMessage
}) => {
  const { t } = useTranslation();
  const allSelected = items.length > 0 && selectedItems.size === items.length;
  const someSelected = selectedItems.size > 0 && selectedItems.size < items.length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-gray-500" />
            <CardTitle className="text-lg">{title}</CardTitle>
          </div>
          <Badge variant="secondary">{items.length} archived</Badge>
        </div>
        <CardDescription>
          {type === 'employees' && 'Archived employees are hidden from team views and cannot log in.'}
          {type === 'customers' && 'Archived customers are hidden from project assignments.'}
          {type === 'projects' && 'Archived projects are hidden from all views and cannot have time logged.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Archive className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>{emptyMessage || `No archived ${type}`}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Bulk Actions Bar */}
            {items.length > 0 && (
              <div className="flex items-center justify-between p-2 bg-gray-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={() => onSelectAll(type)}
                    aria-label={t('auto.set.selectAll')}
                    className={someSelected ? 'opacity-50' : ''}
                  />
                  <span className="text-sm text-gray-600">
                    {selectedItems.size > 0 
                      ? `${selectedItems.size} selected`
                      : 'Select all'}
                  </span>
                </div>
                {selectedItems.size > 0 && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onBulkRestore(type)}
                      className="text-green-600 hover:bg-green-50"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restore ({selectedItems.size})
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onBulkDelete(type)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete ({selectedItems.size})
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {items.map((item) => (
              <ArchivedItemCard
                key={item.id}
                item={item}
                type={type}
                selected={selectedItems.has(item.id)}
                onSelect={onSelectItem}
                onRestore={onRestore}
                onDelete={onDelete}
                primaryField={primaryField}
                secondaryField={secondaryField}
                tertiaryField={tertiaryField}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Archived Items Tab Component
 */
const ArchivedItemsTab = () => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState({
    employees: true,
    customers: true,
    projects: true
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    id: null,
    name: null,
    isBulk: false,
    bulkIds: []
  });
  
  // Selection state for bulk actions
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  
  // Bulk restore dialog state
  const [bulkRestoreDialog, setBulkRestoreDialog] = useState({
    open: false,
    type: null,
    count: 0,
    ids: []
  });

  const fetchArchivedItems = useCallback(async () => {
    setLoading({ employees: true, customers: true, projects: true });
    
    try {
      const [empRes, custRes, projRes] = await Promise.all([
        axios.get(`${API}/archived/employees`),
        axios.get(`${API}/archived/customers`),
        axios.get(`${API}/archived/projects`)
      ]);
      
      setEmployees(empRes.data || []);
      setCustomers(custRes.data || []);
      setProjects(projRes.data || []);
    } catch (err) {
      console.error('Error fetching archived items:', err);
      toast.error(t('auto.set.failedToLoadArchivedItems'));
    } finally {
      setLoading({ employees: false, customers: false, projects: false });
    }
  }, []);

  useEffect(() => {
    fetchArchivedItems();
  }, [fetchArchivedItems]);

  const handleRestore = async (type, id, name) => {
    try {
      await axios.put(`${API}/archived/${type}/${id}/restore`);
      toast.success(`${name} has been restored`);
      fetchArchivedItems();
    } catch (err) {
      console.error('Error restoring item:', err);
      toast.error(err.response?.data?.detail || 'Failed to restore item');
    }
  };

  const handleDeleteClick = (type, id, name) => {
    setDeleteDialog({ open: true, type, id, name });
  };

  const handleDeleteConfirm = async () => {
    const { type, id, name, isBulk, bulkIds } = deleteDialog;
    
    if (isBulk && bulkIds.length > 0) {
      // Handle bulk delete
      let successCount = 0;
      let failCount = 0;
      
      for (const itemId of bulkIds) {
        try {
          await axios.delete(`${API}/archived/${type}/${itemId}/permanent`);
          successCount++;
        } catch (err) {
          console.error(`Error deleting item ${itemId}:`, err);
          failCount++;
        }
      }
      
      if (failCount === 0) {
        toast.success(`Successfully deleted ${successCount} ${type}`);
      } else {
        toast.warning(`Deleted ${successCount}, failed ${failCount}`);
      }
      
      // Clear selection for this type
      clearSelection(type);
    } else {
      // Single item delete
      try {
        await axios.delete(`${API}/archived/${type}/${id}/permanent`);
        toast.success(`${name} has been permanently deleted`);
      } catch (err) {
        console.error('Error deleting item:', err);
        toast.error(err.response?.data?.detail || 'Failed to delete item');
      }
    }
    
    setDeleteDialog({ open: false, type: null, id: null, name: null, isBulk: false, bulkIds: [] });
    fetchArchivedItems();
  };
  
  // Get selection state by type
  const getSelectedItems = (type) => {
    switch (type) {
      case 'employees': return selectedEmployees;
      case 'customers': return selectedCustomers;
      case 'projects': return selectedProjects;
      default: return new Set();
    }
  };
  
  // Clear selection for a type
  const clearSelection = (type) => {
    switch (type) {
      case 'employees': setSelectedEmployees(new Set()); break;
      case 'customers': setSelectedCustomers(new Set()); break;
      case 'projects': setSelectedProjects(new Set()); break;
    }
  };
  
  // Handle individual item selection
  const handleSelectItem = (type, itemId) => {
    const setSelected = type === 'employees' ? setSelectedEmployees 
                      : type === 'customers' ? setSelectedCustomers 
                      : setSelectedProjects;
    
    setSelected(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };
  
  // Handle select all for a type
  const handleSelectAll = (type) => {
    const items = type === 'employees' ? employees 
                : type === 'customers' ? customers 
                : projects;
    const selectedItems = getSelectedItems(type);
    const setSelected = type === 'employees' ? setSelectedEmployees 
                      : type === 'customers' ? setSelectedCustomers 
                      : setSelectedProjects;
    
    if (selectedItems.size === items.length) {
      // Deselect all
      setSelected(new Set());
    } else {
      // Select all
      setSelected(new Set(items.map(item => item.id)));
    }
  };
  
  // Handle bulk restore
  const handleBulkRestore = async (type) => {
    const selectedIds = Array.from(getSelectedItems(type));
    if (selectedIds.length === 0) return;
    
    setBulkRestoreDialog({
      open: true,
      type,
      count: selectedIds.length,
      ids: selectedIds
    });
  };
  
  // Confirm bulk restore
  const handleBulkRestoreConfirm = async () => {
    const { type, ids } = bulkRestoreDialog;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const itemId of ids) {
      try {
        await axios.put(`${API}/archived/${type}/${itemId}/restore`);
        successCount++;
      } catch (err) {
        console.error(`Error restoring item ${itemId}:`, err);
        failCount++;
      }
    }
    
    if (failCount === 0) {
      toast.success(`Successfully restored ${successCount} ${type}`);
    } else {
      toast.warning(`Restored ${successCount}, failed ${failCount}`);
    }
    
    // Clear selection for this type
    clearSelection(type);
    setBulkRestoreDialog({ open: false, type: null, count: 0, ids: [] });
    fetchArchivedItems();
  };
  
  // Handle bulk delete
  const handleBulkDelete = (type) => {
    const selectedIds = Array.from(getSelectedItems(type));
    if (selectedIds.length === 0) return;
    
    setDeleteDialog({
      open: true,
      type,
      id: null,
      name: `${selectedIds.length} ${type}`,
      isBulk: true,
      bulkIds: selectedIds
    });
  };

  const totalArchived = employees.length + customers.length + projects.length;

  return (
    <div className="space-y-6" data-testid="archived-items-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{t('auto.set.archivedItems')}</h2>
          <p className="text-sm text-gray-500">
            Manage archived employees, customers, and projects. 
            Archived items are hidden from regular views but preserved in the database.
          </p>
        </div>
        <Button variant="outline" onClick={fetchArchivedItems} data-testid="btn-fetcharchiveditems">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('auto.set.refresh')}
        </Button>
      </div>

      {/* Summary */}
      {totalArchived > 0 && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">
              {totalArchived} archived item{totalArchived !== 1 ? 's' : ''} found
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              {t('auto.set.permanentlyDeletingItemsCannotBeUndone')}
            </p>
          </div>
        </div>
      )}

      {/* Tabs for each type */}
      <Tabs defaultValue="employees" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="employees" className="flex items-center gap-2" data-testid="tab-employees">
            <Users className="h-4 w-4" />
            {t('auto.set.employees')}
            {employees.length > 0 && (
              <Badge variant="secondary" className="ml-1">{employees.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="customers" className="flex items-center gap-2" data-testid="tab-customers">
            <Building2 className="h-4 w-4" />
            {t('auto.set.customers')}
            {customers.length > 0 && (
              <Badge variant="secondary" className="ml-1">{customers.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="projects" className="flex items-center gap-2" data-testid="tab-projects">
            <FolderKanban className="h-4 w-4" />
            {t('auto.set.projects')}
            {projects.length > 0 && (
              <Badge variant="secondary" className="ml-1">{projects.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="employees">
          <ArchivedItemsSection
            title={t('auto.set.archivedEmployees')}
            icon={Users}
            items={employees}
            type="employees"
            loading={loading.employees}
            selectedItems={selectedEmployees}
            onSelectItem={(id) => handleSelectItem('employees', id)}
            onSelectAll={() => handleSelectAll('employees')}
            onRestore={handleRestore}
            onDelete={handleDeleteClick}
            onBulkRestore={() => handleBulkRestore('employees')}
            onBulkDelete={() => handleBulkDelete('employees')}
            primaryField="name"
            secondaryField="email"
            tertiaryField="employee_code"
            emptyMessage="No archived employees. Active employees can be archived from the Employee tab."
          />
        </TabsContent>

        <TabsContent value="customers">
          <ArchivedItemsSection
            title={t('auto.set.archivedCustomers')}
            icon={Building2}
            items={customers}
            type="customers"
            loading={loading.customers}
            selectedItems={selectedCustomers}
            onSelectItem={(id) => handleSelectItem('customers', id)}
            onSelectAll={() => handleSelectAll('customers')}
            onRestore={handleRestore}
            onDelete={handleDeleteClick}
            onBulkRestore={() => handleBulkRestore('customers')}
            onBulkDelete={() => handleBulkDelete('customers')}
            primaryField="name"
            secondaryField="contact_person"
            tertiaryField="customer_code"
            emptyMessage="No archived customers. Active customers can be archived from the Customers tab."
          />
        </TabsContent>

        <TabsContent value="projects">
          <ArchivedItemsSection
            title={t('auto.set.archivedProjects')}
            icon={FolderKanban}
            items={projects}
            type="projects"
            loading={loading.projects}
            selectedItems={selectedProjects}
            onSelectItem={(id) => handleSelectItem('projects', id)}
            onSelectAll={() => handleSelectAll('projects')}
            onRestore={handleRestore}
            onDelete={handleDeleteClick}
            onBulkRestore={() => handleBulkRestore('projects')}
            onBulkDelete={() => handleBulkDelete('projects')}
            primaryField="name"
            secondaryField="description"
            tertiaryField="project_code"
            emptyMessage="No archived projects. Active projects can be archived from the Projects tab."
          />
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog(prev => ({ ...prev, open: false  }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {t('auto.set.permanentlyDelete')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-3">
                Are you sure you want to permanently delete <strong>{deleteDialog.name}</strong>?
              </p>
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                <strong>{t('auto.set.warning')}</strong> This action cannot be undone. 
                {deleteDialog.type === 'employees' && ' All associated timesheets and availability records will be deleted.'}
                {deleteDialog.type === 'projects' && ' All associated expenses and timesheet entries will be deleted.'}
                {deleteDialog.type === 'customers' && ' Associated projects will be archived.'}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('auto.set.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleteDialog.isBulk ? `Delete ${deleteDialog.bulkIds?.length} Items` : 'Yes, Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Restore Confirmation Dialog */}
      <AlertDialog open={bulkRestoreDialog.open} onOpenChange={(open) => !open && setBulkRestoreDialog(prev => ({ ...prev, open: false  }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-green-600">
              <RotateCcw className="h-5 w-5" />
              Restore {bulkRestoreDialog.count} {bulkRestoreDialog.type}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-3">
                Are you sure you want to restore <strong>{bulkRestoreDialog.count} {bulkRestoreDialog.type}</strong> to active status?
              </p>
              <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                {t('auto.set.theseItemsWillBeVisibleIn')}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('auto.set.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkRestoreConfirm}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-bulk-restore-btn"
            >
              Restore {bulkRestoreDialog.count} Items
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ArchivedItemsTab;
